import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, clearAdminToken } from '../../services/adminApi';
import {
  Loader2, ArrowLeft, Send, Users, FileText, Eye, AlertTriangle,
  CheckCircle2, Mail, History, Plus, Trash2, Calendar,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ConfirmDeleteDialog';
import { useConfirm } from '../../components/useConfirm';

const AUDIENCES = [
  { value: 'all_admins',        label: 'All tenant admins',           hint: 'Every Admin user across every tenant' },
  { value: 'pro_admins',        label: 'Pro plan admins only',        hint: 'Admins in Pro / Enterprise tenants' },
  { value: 'free_admins',       label: 'Free plan admins only',       hint: 'Admins in Free tenants' },
  { value: 'failed_payment',    label: 'Past-due / failed payment',   hint: 'Admins whose card was declined' },
  { value: 'all_office_users',  label: 'All office users',            hint: 'Admins + Fleet Managers (excludes mechanics)' },
  { value: 'specific_tenants',  label: 'Specific tenants…',           hint: 'Paste tenant IDs (one per line)' },
];

const AdminBroadcastPage = () => {
  const { confirm, dialogProps } = useConfirm();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [audience, setAudience] = useState('all_admins');
  const [tenantIdsText, setTenantIdsText] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audienceCount, setAudienceCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('compose'); // 'compose' | 'templates' | 'history'
  const [newsletterDraftOpen, setNewsletterDraftOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, histRes] = await Promise.all([
        adminApi.broadcastTemplates(),
        adminApi.broadcastHistory(),
      ]);
      setTemplates(tplRes.data?.templates || []);
      setHistory(histRes.data?.broadcasts || []);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAdminToken();
        navigate('/admin/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const applyTemplate = (slug) => {
    setSelectedSlug(slug);
    const tpl = templates.find((t) => t.slug === slug);
    if (tpl) {
      setSubject(tpl.subject);
      setBodyHtml(tpl.body_html);
    }
  };

  const parseTenantIds = () =>
    tenantIdsText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const previewAudience = useCallback(async () => {
    try {
      const res = await adminApi.broadcastAudiencePreview({
        audience,
        tenant_ids: audience === 'specific_tenants' ? parseTenantIds() : undefined,
        template_slug: selectedSlug || undefined,
        subject,
        body_html: bodyHtml,
      });
      setAudienceCount(res.data);
    } catch (e) { /* swallow */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, tenantIdsText, selectedSlug]);

  useEffect(() => { previewAudience(); }, [previewAudience]);

  const handleSend = async (dryRun = false) => {
    if (!dryRun) {
      const ok = window.confirm(`Send to ${audienceCount?.recipient_count || 0} recipient(s)?`);
      if (!ok) return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await adminApi.broadcastSend({
        audience,
        tenant_ids: audience === 'specific_tenants' ? parseTenantIds() : undefined,
        template_slug: selectedSlug || undefined,
        subject,
        body_html: bodyHtml,
        dry_run: dryRun,
      });
      setResult(res.data);
      if (!dryRun) {
        await loadAll();
      }
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'Send failed' });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const audienceMeta = AUDIENCES.find((a) => a.value === audience);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-broadcast-page">
      <header className="border-b border-border bg-card/40 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="admin-back-to-tenants"
          >
            <ArrowLeft className="w-4 h-4" /> Tenants
          </button>
          <h1 className="text-base font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Email broadcast
          </h1>
          <div />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-border">
          {[
            { id: 'compose',   label: 'Compose & send', icon: Send },
            { id: 'templates', label: 'Templates',      icon: FileText },
            { id: 'history',   label: 'History',        icon: History },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  tab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`broadcast-tab-${t.id}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'compose' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Editor */}
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-md border border-border bg-card p-5 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Template (optional)</label>
                  <select
                    value={selectedSlug}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
                    data-testid="broadcast-template-select"
                  >
                    <option value="">— Custom (write your own) —</option>
                    {templates.map((t) => (
                      <option key={t.slug} value={t.slug}>
                        {t.is_builtin ? '★ ' : ''}{t.name} <span style={{ color: '#666' }}>({t.category})</span>
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="[EQUIPR Guardian] ..."
                    className="mt-1 w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
                    data-testid="broadcast-subject-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">
                    Body (HTML) <span className="text-muted-foreground/60">— supports &#123;&#123;admin_name&#125;&#125;, &#123;&#123;company_name&#125;&#125;, &#123;&#123;billing_url&#125;&#125;, &#123;&#123;help_url&#125;&#125;</span>
                  </label>
                  <textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    rows={12}
                    className="mt-1 w-full px-3 py-2 rounded-md bg-background border border-border text-xs font-mono"
                    data-testid="broadcast-body-input"
                  />
                </div>
              </div>

              {/* Preview — always rendered so the placeholder reflects state */}
              <div className="rounded-md border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Eye className="w-4 h-4 text-primary" />
                  <h3 className="text-sm uppercase tracking-widest font-semibold">Preview</h3>
                  <span className="text-xs text-muted-foreground">— with sample placeholders filled in</span>
                </div>
                {bodyHtml ? (
                  <div
                    className="prose prose-invert prose-sm max-w-none p-4 rounded bg-background/60 border border-border/40"
                    data-testid="broadcast-preview"
                    dangerouslySetInnerHTML={{
                      __html: bodyHtml
                        .replace(/\{\{admin_name\}\}/g, 'Jamie')
                        .replace(/\{\{company_name\}\}/g, 'Acme Cranes Pty Ltd')
                        .replace(/\{\{billing_url\}\}/g, 'https://guardian.equiprsoftware.com/settings/billing')
                        .replace(/\{\{help_url\}\}/g, 'https://guardian.equiprsoftware.com'),
                    }}
                  />
                ) : (
                  <div
                    className="p-6 rounded bg-background/40 border border-dashed border-border/40 text-center text-xs text-muted-foreground"
                    data-testid="broadcast-preview"
                  >
                    Pick a template or start writing on the left — your email will preview here with sample placeholders.
                  </div>
                )}
              </div>
            </div>

            {/* Audience + Send */}
            <div className="space-y-4">
              <div className="rounded-md border border-border bg-card p-5 space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Audience</label>
                  <select
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
                    data-testid="broadcast-audience-select"
                  >
                    {AUDIENCES.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">{audienceMeta?.hint}</p>
                </div>

                {audience === 'specific_tenants' && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Tenant IDs (one per line)</label>
                    <textarea
                      value={tenantIdsText}
                      onChange={(e) => setTenantIdsText(e.target.value)}
                      rows={4}
                      className="mt-1 w-full px-3 py-2 rounded-md bg-background border border-border text-xs font-mono"
                      data-testid="broadcast-tenant-ids-input"
                    />
                  </div>
                )}

                {audienceCount && (
                  <div className="rounded-sm border border-primary/30 bg-primary/5 p-3" data-testid="broadcast-audience-count">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">{audienceCount.recipient_count} recipient{audienceCount.recipient_count === 1 ? '' : 's'}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Across {audienceCount.tenant_count} tenant{audienceCount.tenant_count === 1 ? '' : 's'}
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleSend(true)}
                    disabled={sending || !subject || !bodyHtml}
                    className="flex-1 h-10 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-50 text-sm flex items-center justify-center gap-2"
                    data-testid="broadcast-dry-run-btn"
                  >
                    <Eye className="w-4 h-4" /> Dry run
                  </button>
                  <button
                    onClick={() => handleSend(false)}
                    disabled={sending || !subject || !bodyHtml || !audienceCount?.recipient_count}
                    className="flex-1 h-10 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 text-sm flex items-center justify-center gap-2 font-semibold"
                    data-testid="broadcast-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send
                  </button>
                </div>
              </div>

              {result && (
                <div
                  className={`rounded-md border p-4 ${result.error ? 'border-red-500/50 bg-red-500/5' : 'border-emerald-500/50 bg-emerald-500/5'}`}
                  data-testid="broadcast-result"
                >
                  {result.error ? (
                    <div className="flex items-center gap-2 text-red-300 text-sm">
                      <AlertTriangle className="w-4 h-4" /> {result.error}
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-emerald-300 text-sm">
                      <CheckCircle2 className="w-4 h-4 mt-0.5" />
                      <div>
                        <p className="font-semibold">
                          {result.dry_run ? 'Dry run complete' : `Sent to ${result.sent} recipient${result.sent === 1 ? '' : 's'}`}
                        </p>
                        {result.failed > 0 && (
                          <p className="text-xs text-amber-300 mt-1">⚠ {result.failed} failed delivery</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'templates' && (
          <div className="space-y-4">
            {/* "Draft this month's newsletter" — pre-fills slug + starter
                content so Anthony can publish in one click. Lifecycle cron
                picks it up on days 1-3 of the matching month. */}
            <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-5 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold uppercase tracking-widest">Monthly newsletter</h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Draft this month&apos;s &ldquo;What&apos;s new&rdquo; digest — the lifecycle cron will send it on the 1st of the month to every tenant admin.
                </p>
              </div>
              <button
                onClick={() => setNewsletterDraftOpen(true)}
                className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary text-black text-xs font-semibold uppercase tracking-wider hover:bg-primary/90"
                data-testid="draft-newsletter-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Draft this month
              </button>
            </div>

          <div className="rounded-md border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.slug} className="border-t border-border" data-testid={`template-row-${t.slug}`}>
                    <td className="p-3 font-medium">{t.name}</td>
                    <td className="p-3 text-xs uppercase tracking-widest text-muted-foreground">{t.category}</td>
                    <td className="p-3 text-xs">{t.subject}</td>
                    <td className="p-3">
                      {t.is_builtin
                        ? <span className="text-xs text-primary">Built-in</span>
                        : <span className="text-xs text-muted-foreground">Custom</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        onClick={() => { applyTemplate(t.slug); setTab('compose'); }}
                        className="text-xs text-primary hover:underline mr-3"
                        data-testid={`use-template-${t.slug}`}
                      >
                        Use →
                      </button>
                      {!t.is_builtin && (
                        <button
                          onClick={() => confirm({
                            title: `Delete template "${t.name}"?`,
                            description: 'This broadcast template will be permanently removed.',
                            testId: 'confirm-delete-broadcast-template',
                            onConfirm: async () => {
                              await adminApi.broadcastDeleteTemplate(t.slug);
                              await loadAll();
                            },
                          })}
                          className="text-xs text-red-400 hover:underline"
                          data-testid={`delete-template-${t.slug}`}
                        >
                          <Trash2 className="w-3 h-3 inline" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="rounded-md border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Subject</th>
                  <th className="text-left p-3">Audience</th>
                  <th className="text-right p-3">Sent</th>
                  <th className="text-right p-3">Failed</th>
                  <th className="text-left p-3">By</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-muted-foreground">No broadcasts yet.</td>
                  </tr>
                ) : history.map((b) => (
                  <tr key={b.id} className="border-t border-border" data-testid={`history-row-${b.id}`}>
                    <td className="p-3 font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="p-3 text-sm">{b.subject}</td>
                    <td className="p-3 text-xs uppercase tracking-widest text-muted-foreground">{b.audience}</td>
                    <td className="p-3 text-right font-mono">{b.sent_count}</td>
                    <td className={`p-3 text-right font-mono ${b.failed_count ? 'text-red-400' : 'text-muted-foreground'}`}>{b.failed_count}</td>
                    <td className="p-3 text-xs">{b.created_by_email || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {newsletterDraftOpen && (
        <NewsletterDraftDialog
          existing={templates}
          onClose={() => setNewsletterDraftOpen(false)}
          onSaved={async () => {
            setNewsletterDraftOpen(false);
            await loadAll();
          }}
        />
      )}
      <ConfirmDeleteDialog {...dialogProps} />
    </div>
  );
};

// ─── Monthly-newsletter draft dialog ──────────────────────────────────────
//
// Pre-fills the slug, name and a starter body so Anthony can publish in
// under a minute. If a draft already exists for the current month, we load
// it for editing instead of starting from scratch.
const _currentMonth = () => {
  const d = new Date();
  return {
    id: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
    label: d.toLocaleString('en-AU', { month: 'long', year: 'numeric' }),
  };
};

const STARTER_BODY_HTML = `<p>Hi,</p>
<p>Here's what's new in Guardian this month:</p>
<ul>
  <li><strong>Feature A</strong> — what it does and why you'd use it.</li>
  <li><strong>Feature B</strong> — another one-liner.</li>
  <li><strong>Tip of the month</strong> — a workflow you might have missed.</li>
</ul>
<p>As always, hit reply if you want a hand setting any of this up.</p>`;

const NewsletterDraftDialog = ({ existing, onClose, onSaved }) => {
  const month = useMemo(() => _currentMonth(), []);
  const slug = `newsletter-${month.id}`;
  const existingTpl = existing.find((t) => t.slug === slug);

  const [subject, setSubject] = useState(
    existingTpl?.subject || `What's new in Guardian — ${month.label}`
  );
  const [bodyHtml, setBodyHtml] = useState(existingTpl?.body_html || STARTER_BODY_HTML);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error('Subject and body are both required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.broadcastSaveTemplate({
        slug,
        name: `Newsletter — ${month.label}`,
        category: 'newsletter',
        subject: subject.trim(),
        body_html: bodyHtml,
      });
      toast.success(existingTpl ? 'Newsletter draft updated' : 'Newsletter draft saved');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save draft');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="newsletter-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            Draft newsletter — {month.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div className="rounded-md bg-muted/20 border border-border px-4 py-3 text-xs text-muted-foreground">
            Slug: <code className="text-primary">{slug}</code>
            {existingTpl
              ? <span className="ml-2 text-amber-300">— editing existing draft</span>
              : <span className="ml-2 text-emerald-300">— new draft</span>}
            <div className="mt-1">
              Lifecycle cron sends this on the 1st-3rd of {month.label} to every tenant admin not opted out of the <code>newsletter</code> sequence.
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Subject</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
              data-testid="newsletter-subject-input"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">
              Body (HTML) <span className="text-muted-foreground/60">— wrapped in Guardian branded shell automatically</span>
            </label>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              rows={14}
              className="mt-1 w-full px-3 py-2 rounded-md bg-background border border-border text-xs font-mono"
              data-testid="newsletter-body-input"
            />
          </div>

          {bodyHtml && (
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Preview</p>
              <div
                className="rounded-md border border-border bg-white text-black p-4 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 px-4 rounded-md border border-border text-sm hover:bg-muted/30 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-9 px-4 rounded-md bg-primary text-black text-sm font-semibold inline-flex items-center gap-2 hover:bg-primary/90 disabled:opacity-40"
            data-testid="newsletter-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {existingTpl ? 'Update draft' : 'Save draft'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminBroadcastPage;
